import { useId, type ComponentProps, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface FieldProps {
  label: ReactNode
  /** The quiet help line under the control (`.help`). */
  help?: ReactNode
  /** Grid span, when the field sits in `<Grid>`: the prototype's `field col-6`. */
  className?: string
  /** Render-prop: receives the id to put on the control, so the label points at it. */
  children: (id: string) => ReactNode
}

/** A labelled form row (`.field`): label, control, help. */
export function Field({ label, help, className, children }: FieldProps) {
  const id = useId()
  return (
    <div className={cn('field', className)}>
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {help ? <p className="help">{help}</p> : null}
    </div>
  )
}

/** A text input (`.input`). */
export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn('input', className)} {...props} />
}

/** A multi-line input (`textarea.input`). */
export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return <textarea className={cn('input', className)} {...props} />
}

/**
 * A native select. `variant="field"` is the full-width form select (`select.input`);
 * `"filter"` is the compact filter-bar and header select (`.sel`). Both draw the
 * design's chevron.
 */
export function Select({ variant = 'filter', className, ...props }: ComponentProps<'select'> & { variant?: 'field' | 'filter' }) {
  return <select className={cn(variant === 'field' ? 'input' : 'sel', className)} {...props} />
}

interface SwitchProps extends Omit<ComponentProps<'input'>, 'type' | 'onChange'> {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** Required: a switch has no visible label of its own. */
  'aria-label': string
}

/**
 * The toggle switch (`.switch`): a real checkbox with `role="switch"`, laid over
 * the drawn track, which lets the pointer through (console.css), so a click or tap
 * anywhere on it toggles. A `<span>` rather than a `<label>`: a switch often sits
 * inside or beside its own label, and labels must not nest.
 */
export function Switch({ checked, onCheckedChange, className, ...props }: SwitchProps) {
  return (
    <span className={cn('switch', className)}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(event) => onCheckedChange(event.target.checked)}
        {...props}
      />
      <span className="tr" />
    </span>
  )
}

/** The little "i" (`.info`) whose title explains a figure or a label. */
export function Info({ title }: { title: string }) {
  return (
    <span className="info" title={title} aria-label={title} role="img">
      i
    </span>
  )
}
